import React, { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { t, lang, setLang, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const blogLabel = "Blog";

  const links = [
    { href: "/#about", label: t.nav.about },
    { href: "/#products", label: t.nav.products },
    { href: "/#process", label: t.nav.process },
    { href: "/#quality", label: t.nav.quality },
    { href: "/blog", label: blogLabel, route: true },
    { href: "/#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" data-testid="brand-logo" className="flex items-center group">
            <span className="relative inline-flex items-center h-14 sm:h-16 transition-transform duration-500 group-hover:scale-[1.03]">
              <img
                src="/ekobiotek-logo-white.webp"
                alt="EKOBIOTEK Ukraine"
                className={`h-full w-auto object-contain transition-opacity duration-500 ${scrolled ? "opacity-0" : "opacity-100"}`}
                loading="eager"
                decoding="async"
              />
              <img
                src="/ekobiotek-logo.webp"
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 h-full w-auto object-contain transition-opacity duration-500 ${scrolled ? "opacity-100" : "opacity-0"}`}
                loading="eager"
                decoding="async"
              />
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.href.replace(/[/#]/g, "")}`}
                className={`text-sm font-light tracking-wide transition-colors hover:text-[#DE9E36] ${
                  scrolled ? "text-[#1A201C]" : "text-white"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Lang toggle */}
            <div
              className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full border ${
                scrolled ? "border-[#E5E0D8]" : "border-white/40"
              }`}
            >
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-toggle-${l.code}`}
                  className={`px-2.5 py-0.5 text-[11px] tracking-[0.2em] rounded-full transition-all ${
                    lang === l.code
                      ? "bg-[#DE9E36] text-white"
                      : scrolled
                      ? "text-[#4E5750] hover:text-[#1A201C]"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* CTA */}
            <a
              href="#contact"
              data-testid="header-cta"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A201C] text-[#FDFBF7] text-xs tracking-[0.18em] uppercase hover:bg-[#DE9E36] transition-colors"
            >
              {t.nav.cta}
            </a>

            {/* Mobile menu */}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              className={`lg:hidden p-2 ${scrolled ? "text-[#1A201C]" : "text-white"}`}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#E5E0D8]"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base text-[#1A201C] hover:text-[#DE9E36]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-3 border-t border-[#E5E0D8]">
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1 text-xs tracking-[0.2em] rounded-full ${
                    lang === l.code
                      ? "bg-[#DE9E36] text-white"
                      : "border border-[#E5E0D8] text-[#4E5750]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-[#1A201C] text-[#FDFBF7] rounded-full text-xs tracking-[0.18em] uppercase"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
